import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { buildOrgUrl, getPreferredOrgSlug } from '@/core/domain';
import { attemptHandoff } from '@/core/handoff';
import { acceptInvitation } from '@/core/invitation';
import { setToken } from '@/core/token';
import { useMe } from '@/hooks/useMe';
import { analytics } from '@/lib/analytics';
import { normalizeEmail } from '@/lib/email';
import { cn } from '@/lib/utils';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { useFormik } from 'formik';
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { Fingerprint } from 'lucide-react';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthLogo } from './AuthLogo';
import { InputWithError } from './InputWithError';

export const LoginForm = ({ className, ...props }: React.ComponentProps<'div'>) => {
  const navigate = useNavigate();
  const me = useMe();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation('auth');

  // Central-login handoff: if we arrived with ?returnTo=<custom-domain URL>,
  // exchange our fresh session for a one-time code and bounce back there.
  // Falls back to normal navigation when the handoff is rejected (e.g. the
  // user isn't a member of the org owning that domain).
  const tryHandoff = async (): Promise<boolean> => {
    const returnTo = new URLSearchParams(window.location.search).get('returnTo');
    if (!returnTo) return false;
    return attemptHandoff(returnTo);
  };

  const navigateToOrg = (organizationSlugs?: string[]) => {
    const currentSlug = getPreferredOrgSlug(organizationSlugs);
    // If already on a valid org subdomain that the user has access to, stay there.
    // A full load, not navigate('/'): the in-memory query cache still holds what
    // the previous session fetched (e.g. another account's MyOrganizations), and
    // useOrganizations would bounce off this org on that stale list.
    if (currentSlug) {
      if (window.location.hostname.startsWith(`${currentSlug}.`)) window.location.assign('/');
      else window.location.href = buildOrgUrl(currentSlug);
      return;
    }
    // If the user has orgs, redirect to the first one's subdomain
    if (organizationSlugs && organizationSlugs.length > 0) {
      window.location.href = buildOrgUrl(organizationSlugs[0]);
      return;
    }
    // No orgs — go to onboarding
    navigate('/auth/onboarding/org');
  };

  const setParams = (params: Record<string, string | null | undefined | number>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (String(value)) newParams.set(key, String(value));
      else newParams.delete(key);
    });
    setSearchParams(newParams);
  };

  const handlePasskeyLogin = async () => {
    setIsLoading(true);
    setError(null);
    const toastId = toast.loading(t('login.authenticatingPasskey'));
    try {
      // Get authentication options (discoverable credentials, no email needed)
      const optionsRes = await fetch(`${import.meta.env.VITE_ENDPOINT}/passkey/login-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const optionsData = await optionsRes.json();
      if (!optionsRes.ok) throw new Error(optionsData.error || 'Failed to get passkey options');

      const credential = await startAuthentication({ optionsJSON: optionsData.options });

      const verifyRes = await fetch(`${import.meta.env.VITE_ENDPOINT}/passkey/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential,
          challenge: optionsData.challenge,
          challengeSignature: optionsData.challengeSignature,
        }),
      });
      const result = await verifyRes.json();
      if (!result.success) throw new Error(result.error || 'Passkey verification failed');

      setToken(result.token);
      me.loadMe();
      analytics.capture('Login Successful', { method: 'passkey' });
      toast.success(t('login.loginSuccessful'), { id: toastId });
      if (await tryHandoff()) return;
      const invitation = searchParams.get('invitation');
      if (invitation) navigate(await acceptInvitation(invitation));
      else navigateToOrg(result.organizationSlugs);
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        toast.dismiss(toastId);
      } else {
        const msg = err.message || 'Passkey login failed';
        setError(msg);
        toast.error(msg, { id: toastId });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get values from URL params with defaults
  const loginMode = (searchParams.get('mode') as 'otp' | 'password') || 'otp';
  const otpSecret = searchParams.get('secret');
  const otpSent = !!otpSecret; // OTP is sent if secret exists

  // 2FA state
  const twoFactorSecret = searchParams.get('twoFactorSecret');
  const twoFactorType = searchParams.get('twoFactorType') as 'SMS' | 'AUTHENTICATOR' | null;
  const twoFactorRequired = !!twoFactorSecret || twoFactorType === 'AUTHENTICATOR';

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      otp: '',
      twoFactorCode: '',
    },
    validate: (values) => {
      const errors: any = {};

      if (twoFactorRequired && !values.twoFactorCode) {
        errors.twoFactorCode = t('login.enterVerificationCode');
      }
      if (twoFactorRequired && values.twoFactorCode && values.twoFactorCode.length !== 6) {
        errors.twoFactorCode = t('login.codeMust6Chars');
      }

      if (!twoFactorRequired && loginMode === 'otp' && otpSecret && !values.otp) {
        errors.otp = t('login.enterOtpCode');
      }

      if (!twoFactorRequired && loginMode === 'otp' && otpSecret && values.otp && values.otp.length !== 6) {
        errors.otp = t('login.passcodeMust6Chars');
      }

      return errors;
    },
    onSubmit: async (values) => {
      const email = normalizeEmail(values.email);
      setIsLoading(true);
      setError(null);

      // Handle 2FA verification
      if (twoFactorRequired) {
        const toastId = toast.loading(t('login.verifying2fa'), {
          description: t('login.verifying2faDescription'),
        });

        try {
          const captchaToken = await executeRecaptcha?.('verify_2fa');
          const res = await fetch(`${import.meta.env.VITE_ENDPOINT}/verify-2fa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },

            body: JSON.stringify({
              twoFactorSecret: twoFactorSecret || undefined,
              twoFactorType: twoFactorType || 'SMS',
              email: email || searchParams.get('email') || undefined,
              code: values.twoFactorCode,
              captchaToken,
            }),
          });

          const data = await res.json();

          if (!res.ok) {
            formik.setFieldError('twoFactorCode', data.error || t('login.invalidCode'));
            throw new Error(data.error || t('login.invalidCode'));
          }

          if (data.success && data.token) {
            setToken(data.token);
            me.loadMe();
            analytics.capture('Login Successful', { method: '2fa', twoFactorType: twoFactorType || 'SMS' });
            toast.success(t('login.loginSuccessful'), {
              description: t('login.welcomeBackDescription'),
              id: toastId,
            });
            if (await tryHandoff()) return;
            const invitation = searchParams.get('invitation');
            if (invitation) navigate(await acceptInvitation(invitation));
            else navigateToOrg(data.organizationSlugs);
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : t('login.invalidCode');
          setError(errorMessage);
          toast.error(t('login.verificationFailed'), {
            description: errorMessage,
            id: toastId,
          });
        } finally {
          setIsLoading(false);
        }
        return;
      }

      if (loginMode === 'otp') {
        if (!otpSecret) {
          // Send OTP
          const toastId = toast.loading(t('login.sendingPasscode'), {
            description: t('login.sendingPasscodeDescription'),
          });

          try {
            const captchaToken = await executeRecaptcha?.('login');

            const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },

              body: JSON.stringify({
                email,
                captchaToken,
                method: 'OTP',
              }),
            });

            const data = await response.json();

            if (!response.ok) {
              if (data.error === 'EMAIL_NOT_VERIFIED' && data.secret) {
                toast.info(t('login.verifyEmailFirst'), { id: toastId });
                const params = new URLSearchParams({ secret: data.secret, email: data.email || email });
                const returnTo = new URLSearchParams(window.location.search).get('returnTo');
                if (returnTo) params.set('returnTo', returnTo);
                navigate(`/auth/verify?${params.toString()}`);
                return;
              }
              throw new Error(data.error || t('login.failedSendPasscode'));
            }

            if (data.success) {
              // Store the secret in URL params (otpSent is determined by secret presence)
              setParams({
                mode: 'otp',
                secret: data.secret,
              });
              toast.success(t('login.passcodeSent'), {
                description: t('login.passcodeSentDescription'),
                id: toastId,
              });
            } else {
              throw new Error('Invalid response from server');
            }
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('login.failedSendPasscode');
            setError(errorMessage);
            toast.error(t('login.failedSendPasscode'), {
              description: errorMessage,
              id: toastId,
            });
          } finally {
            setIsLoading(false);
          }
        } else {
          // Verify OTP
          const toastId = toast.loading(t('login.verifyingPasscode'), {
            description: t('login.verifyingPasscodeDescription'),
          });

          try {
            const captchaToken = await executeRecaptcha?.('login');
            const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },

              body: JSON.stringify({
                email,
                otp: values.otp,
                secret: otpSecret,
                captchaToken,
                method: 'OTP',
              }),
            });

            const data = await response.json();

            if (!response.ok) {
              // Set formik error for OTP field
              formik.setFieldError('otp', data.error || t('login.invalidPasscode'));
              throw new Error(data.error || t('login.invalidPasscode'));
            }

            if (data.success && data.twoFactorRequired) {
              const isAuthenticator = data.twoFactorType === 'AUTHENTICATOR';
              toast.info(t('login.twoFactorRequired'), {
                description: isAuthenticator ? t('login.twoFactorAuthenticator') : t('login.twoFactorSms'),
                id: toastId,
              });
              setParams({
                twoFactorSecret: data.twoFactorSecret || null,
                twoFactorType: data.twoFactorType || 'SMS',
                email,
              });
            } else if (data.success && data.token) {
              setToken(data.token);
              me.loadMe();
              analytics.capture('Login Successful', { method: 'otp' });
              toast.success(t('login.loginSuccessful'), {
                description: t('login.welcomeBackDescription'),
                id: toastId,
              });
              if (await tryHandoff()) return;
              const invitation = searchParams.get('invitation');
              if (invitation) navigate(await acceptInvitation(invitation));
              else navigateToOrg(data.organizationSlugs);
            } else {
              throw new Error('Invalid response from server');
            }
          } catch (err) {
            const errorMessage = err instanceof Error ? err.message : t('login.invalidPasscode');
            setError(errorMessage);
            toast.error(t('login.invalidPasscode'), {
              description: errorMessage,
              id: toastId,
            });
          } finally {
            setIsLoading(false);
          }
        }
      } else {
        // Password login
        const toastId = toast.loading(t('login.loggingIn'), {
          description: t('login.loggingInDescription'),
        });

        try {
          const captchaToken = await executeRecaptcha?.('login');
          const response = await fetch(`${import.meta.env.VITE_ENDPOINT}/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },

            body: JSON.stringify({
              email,
              password: values.password,
              captchaToken,
              method: 'PASSWORD',
            }),
          });

          const data = await response.json();

          if (!response.ok) {
            if (data.error === 'EMAIL_NOT_VERIFIED' && data.secret) {
              toast.info(t('login.verifyEmailFirst'), { id: toastId });
              const params = new URLSearchParams({ secret: data.secret, email: data.email || email });
              const returnTo = new URLSearchParams(window.location.search).get('returnTo');
              if (returnTo) params.set('returnTo', returnTo);
              navigate(`/auth/verify?${params.toString()}`);
              return;
            }
            throw new Error(data.error || t('login.loginFailed'));
          }

          if (data.success && data.twoFactorRequired) {
            const isAuthenticator = data.twoFactorType === 'AUTHENTICATOR';
            toast.info(t('login.twoFactorRequired'), {
              description: isAuthenticator ? t('login.twoFactorAuthenticator') : t('login.twoFactorSms'),
              id: toastId,
            });
            setParams({
              twoFactorSecret: data.twoFactorSecret || null,
              twoFactorType: data.twoFactorType || 'SMS',
              email,
            });
          } else if (data.success && data.token) {
            setToken(data.token);
            me.loadMe();
            analytics.capture('Login Successful', { method: 'password' });
            toast.success(t('login.loginSuccessful'), {
              description: t('login.welcomeBackDescription'),
              id: toastId,
            });
            if (await tryHandoff()) return;
            const invitation = searchParams.get('invitation');
            if (invitation) navigate(await acceptInvitation(invitation));
            else navigateToOrg(data.organizationSlugs);
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : t('login.loginFailed');
          setError(errorMessage);
          toast.error(t('login.loginFailed'), {
            description: errorMessage,
            id: toastId,
          });
        } finally {
          setIsLoading(false);
        }
      }
    },
  });

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Card className='overflow-hidden p-0'>
        <CardContent className='grid p-0 md:grid-cols-2 bg-white dark:bg-stone-900'>
          <form className='p-6 md:p-8' onSubmit={formik.handleSubmit}>
            <div className='flex flex-col gap-6'>
              <div className='flex flex-col items-center text-center'>
                <AuthLogo />
                <h1 className='text-2xl font-bold'>{t('login.welcomeBack')}</h1>
                <p className='text-muted-foreground text-balance'>
                  {twoFactorRequired
                    ? twoFactorType === 'AUTHENTICATOR'
                      ? t('login.enterAuthenticatorCode')
                      : t('login.enterPhoneCode')
                    : loginMode === 'otp'
                      ? otpSent
                        ? t('login.enterOtp')
                        : t('login.loginToAccount')
                      : t('login.loginToAccount')}
                </p>
                {error && (
                  <div className='mt-4 p-3 bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-800 rounded-md'>
                    <p className='text-sm text-red-600 dark:text-red-400'>{error}</p>
                  </div>
                )}
              </div>
              {twoFactorRequired ? (
                <div className='grid gap-2'>
                  <Label>{t('login.verificationCode')}</Label>
                  <InputOTP
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                    value={formik.values.twoFactorCode}
                    onChange={(value) => {
                      formik.setFieldValue('twoFactorCode', value);
                      if (formik.errors.twoFactorCode) {
                        formik.setFieldError('twoFactorCode', '');
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') formik.handleSubmit();
                    }}>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                  {formik.touched.twoFactorCode && formik.errors.twoFactorCode && (
                    <p className='text-sm text-destructive'>{formik.errors.twoFactorCode}</p>
                  )}
                </div>
              ) : (
                <>
                  <InputWithError
                    label={t('login.email')}
                    id='email'
                    name='email'
                    type='email'
                    placeholder={t('login.emailPlaceholder')}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email ? formik.errors.email : undefined}
                    touched={formik.touched.email}
                    required
                    disabled={!!otpSecret && loginMode === 'otp'}
                  />

                  {loginMode === 'otp' && otpSecret && (
                    <div className='grid gap-2'>
                      <Label>{t('login.otp')}</Label>
                      <InputOTP
                        maxLength={6}
                        inputMode='text'
                        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                        value={formik.values.otp}
                        onChange={(value) => {
                          formik.setFieldValue('otp', value);
                          if (formik.errors.otp) {
                            formik.setFieldError('otp', '');
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') formik.handleSubmit();
                        }}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                      {formik.touched.otp && formik.errors.otp && (
                        <p className='text-sm text-destructive'>{formik.errors.otp}</p>
                      )}
                    </div>
                  )}

                  {loginMode === 'password' && (
                    <div className='grid gap-2'>
                      <div className='flex items-center'>
                        <Label htmlFor='password'>{t('login.password')}</Label>
                        <Link
                          to='/auth/forgot'
                          className='ml-auto text-sm underline-offset-2 hover:underline'
                          tabIndex={1}>
                          {t('login.forgotPassword')}
                        </Link>
                      </div>
                      <InputWithError
                        id='password'
                        name='password'
                        type='password'
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.password ? formik.errors.password : undefined}
                        touched={formik.touched.password}
                        required
                        tabIndex={0}
                      />
                    </div>
                  )}
                </>
              )}
              <div className='flex flex-col gap-2'>
                <Button type='submit' className='w-full' disabled={isLoading}>
                  {isLoading
                    ? twoFactorRequired
                      ? t('login.verifying')
                      : loginMode === 'otp'
                        ? otpSecret
                          ? t('login.verifying')
                          : t('login.sending')
                        : t('login.loggingIn')
                    : twoFactorRequired
                      ? t('login.verifyCode')
                      : loginMode === 'otp'
                        ? otpSecret
                          ? t('login.verifyPasscode')
                          : t('login.sendPasscode')
                        : t('login.login')}
                </Button>
                {!twoFactorRequired && (
                  <div className='flex gap-2'>
                    {loginMode === 'otp' ? (
                      <Button
                        type='button'
                        variant='link'
                        className='flex-1'
                        onClick={() => {
                          setParams({ mode: 'password' });
                          formik.resetForm({
                            values: { email: formik.values.email, password: '', otp: '', twoFactorCode: '' },
                          });
                          setError(null);
                        }}
                        disabled={isLoading}>
                        {t('login.loginWithPassword')}
                      </Button>
                    ) : (
                      <Button
                        type='button'
                        variant='link'
                        className='flex-1'
                        onClick={() => {
                          setParams({ mode: 'otp' });
                          formik.resetForm();
                          setError(null);
                        }}
                        disabled={isLoading}>
                        {t('login.useOtp')}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {!twoFactorRequired && (
                <div className='text-center text-sm'>
                  {t('login.noAccount')}{' '}
                  <Link to='/auth/signup' className='underline underline-offset-4 hover:text-primary'>
                    {t('login.signUp')}
                  </Link>
                </div>
              )}

              {!twoFactorRequired && browserSupportsWebAuthn() && (
                <>
                  <div className='after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t'>
                    <span className='bg-card text-muted-foreground relative z-10 px-2'>
                      {t('login.orContinueWith')}
                    </span>
                  </div>
                  <Button
                    type='button'
                    variant='outline'
                    className='w-full'
                    onClick={handlePasskeyLogin}
                    disabled={isLoading}>
                    <Fingerprint className='size-4 mr-2' />
                    {t('login.signInWithPasskey')}
                  </Button>
                </>
              )}
            </div>
          </form>
          <div className='bg-muted relative hidden md:block p-4'>
            <div className="absolute inset-0 p-4">

            <img
              src='/login.png'
              alt='Image'
              className='w-full h-full object-contain '
            />
            </div>
          </div>
        </CardContent>
      </Card>
      <div className='text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4'>
        By clicking continue, you agree to our <a href='https://skygis.cloud/terms'>{t('login.termsOfService')}</a> and{' '}
        <a href='https://skygis.cloud/privacy'>{t('login.privacyPolicy')}</a>.
      </div>
    </div>
  );
};
